import React from 'react';
import { useSelector } from 'react-redux';
import { isNkfAecEnabled, isNkfAecReady, isNkfAecSettling, isNkfAecStalled } from '../../../nkf-aec/functions';
/**
 * Unique root class so the injected <style> stays scoped.
 */
const ROOT_CLASS = 'deepai-notices';
/**
 * Sits directly under the self-view tile (#filmstripLocalVideo), which is a
 * fixed 188x118 box at the top-right of the stage.
 *
 * Why here rather than the toolbar or the tab title: both of those were tried
 * and neither reached the user. The toolbar badge is a 9px dot competing with
 * ten other icons, and the tab title is only visible when the meeting is NOT
 * the focused window. A notice stacked under your own camera is in the one spot
 * you already look at to check yourself.
 */
const STYLE = `
.${ROOT_CLASS}{
  position:fixed;
  top:calc(var(--dai-top) + var(--dai-gap) + 8px + 118px + 8px);
  right:calc(var(--dai-col) + var(--dai-gap) * 2 + 12px);
  width:188px;
  z-index:6;
  display:flex; flex-direction:column; gap:6px;
  font-family:var(--font); pointer-events:none;
}
.${ROOT_CLASS} .notice{
  display:flex; align-items:flex-start; gap:7px;
  padding:8px 9px; border-radius:var(--radius-sm, 8px);
  font-size:11.5px; font-weight:600; line-height:1.45;
  border:1px solid;
  /* Opaque on purpose: over the stage gradient a translucent fill washed the
     text out, and this is the one panel that has to stay readable. */
  box-shadow:0 2px 10px rgba(0,0,0,.35);
}
.${ROOT_CLASS} .notice .dot{
  width:8px; height:8px; border-radius:50%; flex:0 0 auto; margin-top:3px;
}
.${ROOT_CLASS} .notice .body{ min-width:0; }
.${ROOT_CLASS} .notice .sub{
  display:block; font-weight:500; font-size:10.5px; color:#c8d0dc; margin-top:3px;
}

/* 準備中 — まだエコーの位置が分からない */
.${ROOT_CLASS} .notice.wait{ color:#ffc861; background:#2a2110; border-color:#f5a524; }
.${ROOT_CLASS} .notice.wait .dot{ background:#f5a524; }

/* 調整中 — 位置は分かった。放っておけば終わるので警告色にしない */
.${ROOT_CLASS} .notice.settle{ color:#8fbaff; background:#101f33; border-color:#3b82f6; }
.${ROOT_CLASS} .notice.settle .dot{ background:#3b82f6; }

/* 異常 — 自然には直らない */
.${ROOT_CLASS} .notice.fault{ color:#ff9a9a; background:#331414; border-color:#ef4444; }
.${ROOT_CLASS} .notice.fault .dot{ background:#ef4444; animation:dai-notice-blink 1s steps(2,start) infinite; }

@keyframes dai-notice-blink{ 0%{ opacity:1; } 100%{ opacity:.35; } }

@media (max-width:900px){
  .${ROOT_CLASS}{
    top:calc(var(--dai-top) + var(--dai-gap) + 4px + 58px + 6px);
    right:calc(var(--dai-gap) + 4px);
    width:172px;
  }
}
`;
/**
 * Notice stack under the self-view.
 *
 * Renders nothing at all once there is nothing to say — an always-present
 * "everything is fine" box trains people to stop reading it.
 *
 * @returns {JSX.Element | null}
 */
export default function DeepaiNoticeSection() {
    const enabled = useSelector(isNkfAecEnabled);
    const ready = useSelector(isNkfAecReady);
    const settling = useSelector((state) => isNkfAecSettling(state));
    const stalled = useSelector((state) => isNkfAecStalled(state));
    // While the mic is held silent the far side hears nothing, so lead with that
    // rather than with the echo-canceller state that causes it.
    const notices = [];
    if (enabled && stalled) {
        notices.push({
            kind: 'fault',
            body: '音声を送っていません',
            sub: 'エコー対策が動作していません。マイクを選び直すと復帰することがあります。'
        });
    }
    else if (enabled && settling) {
        notices.push({
            kind: 'settle',
            body: '音声を送っていません',
            sub: 'エコーの位置を特定しました。調整が終わり次第、自動で送信を始めます。'
        });
    }
    else if (enabled && !ready) {
        notices.push({
            kind: 'wait',
            body: '音声を送っていません',
            sub: 'エコーの位置を測っています。相手にはまだ声が届きません。'
        });
    }
    if (!notices.length) {
        return null;
    }
    return (React.createElement("div", { className: ROOT_CLASS },
        React.createElement("style", null, STYLE),
        notices.map(n => (React.createElement("div", { className: `notice ${n.kind}`, key: n.kind },
            React.createElement("span", { className: 'dot' }),
            React.createElement("span", { className: 'body' },
                n.body,
                React.createElement("span", { className: 'sub' }, n.sub)))))));
}
